import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, pipe } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthenticateResponseDto, GetCurrentLoginInformationOutput, SessionService, TokenAuthService } from 'src/app/api-client';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private tokenAuthService: TokenAuthService,
    private sessionService: SessionService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  // public methods
  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.tokenAuthService.apiTokenAuthAuthenticatePost({
      userName: email,
      password: password
    }).pipe(
      map((auth: AuthenticateResponseDto) => {
        let authModel = new AuthModel();
        authModel.setAuthFromParam(auth.accessToken!, '', auth.expiresIn!);
        const result = this.setAuthFromLocalStorage(authModel);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageToken);
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.sessionService.apiSessionGetCurrentLoginInformationGet().pipe(
      map((session: GetCurrentLoginInformationOutput) => {
        if (session.user) {
          //setting other values temporarily as these will be removed when template is cleaned or api is updated
          let userModel: any = {
            id: session.user.id!,
            username: session.user.userName!,
            email: session.user.emailAddress!,
            authToken: auth.authToken,
            refreshToken: '',
            pic: './assets/media/svg/avatars/blank.svg',
            fullname: session.user.name + " " + session.user.surname,
            firstname: session.user.name!,
            lastname: session.user.surname!,
            occupation: 'CEO',
            companyName: '',
            phone: '',
            language: 'en',
            timeZone: '',
            website: '',
            communication: {
              email: true,
              sms: true,
              phone: false,
            },
            address: {
              addressLine: 'L-12-20 Vertex, Cybersquare',
              city: 'San Francisco',
              state: 'California',
              postCode: '45000',
            },
            socialNetworks: {
              linkedIn: 'https://linkedin.com/admin',
              facebook: 'https://facebook.com/admin',
              twitter: 'https://twitter.com/admin',
              instagram: 'https://instagram.com/admin',
            },
            assignedLocations: session.user.assignedLocations,
            roles: session.user.roles
          };

          this.currentUserSubject.next(userModel);
          return userModel;
        } else {
          this.logout();
        }
        return undefined;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password!)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  getAuthToken(): string {
    return this.getAuthFromLocalStorage()?.authToken ?? undefined!;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
